import { useDebouncedValue } from './useDebouncedValue';
import useInputValue from './useInputValue';

/**
 * This hook expands on useInputValue by adding a debounced value.
 * This is useful for passing to the dependency array of a useCallback
 * which we are passing to useAsyncRequest, as it ensures that the value
 * is updated in real-time for the user, but so that we don't spam the
 * server with requests.
 *
 * @param {string} [initialValue] - Initial value on component mount.
 * @param {number} [debounceTimeout] - The debounce timeout in milliseconds. When omitted,
 *   `useDebouncedValue`'s default applies.
 */
const useDebouncedInputValue = (initialValue: string = '', debounceTimeout?: number) => {
  const [value, onChange] = useInputValue(initialValue);
  const { debouncedValue } = useDebouncedValue(value, debounceTimeout);

  return {
    //  This is the real value of the input, updated in real-time.
    value,
    //  This is the change handler for the input.
    onChange,
    //  This represents the value after the debounce timeout.
    debouncedValue,
  };
};

export default useDebouncedInputValue;
